import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import styled, { keyframes } from 'styled-components';
import SEO from '../components/SEO';
import BackgroundDesign from '../components/BackgroundDesign';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faPhone, faEnvelope, faPaperPlane, faCheckCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const PageHero = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex ;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
     background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.1)),
              url(/images/photo_5802988364859820042_y.jpg) center/cover no-repeat;
    
    filter: brightness(0.3);
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 150px;
    background: linear-gradient(to top, ${props => props.theme.colors.background}, transparent);
    z-index: 2;
  }
`;

const PageHeroContent = styled.div`
  position: relative;
  z-index: 3;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 2rem;
  animation: ${fadeUp} 1s ease-out;
`;

const PageTitle = styled.h1`
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  span {
    color: ${props => props.theme.colors.primary};
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  opacity: 0.9;
  margin-bottom: 2rem;
`;

const MainContent = styled.main`
  flex: 1;
  width: 100%;
  padding: 10rem 0;
  position: relative;
  z-index: 1;
`;

const ContactContainer = styled.div`
  max-width: 1200px;
  margin: -150px auto 0;
  padding: 4rem 2rem;
  position: relative;
  z-index: 3;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 3rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  background: white;
  padding: 3rem;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  height: fit-content;
  animation: ${fadeUp} 0.8s ease-out;
`;

const InfoTitle = styled.h2`
  font-size: 2rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: 2rem;
  font-weight: 700;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  
  .icon-wrapper {
    width: 50px;
    height: 50px;
    background: ${props => props.theme.colors.primary}11;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    
    svg {
      color: ${props => props.theme.colors.primary};
      font-size: 1.2rem;
    }
  }
  
  .content {
    flex: 1;
    
    h3 {
      font-size: 1.2rem;
      color: ${props => props.theme.colors.text};
      margin-bottom: 0.5rem;
    }
    
    p {
      color: #666;
      line-height: 1.6;
    }
  }
`;

const ContactForm = styled.form`
  background: white;
  padding: 3rem;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  animation: ${fadeUp} 0.8s ease-out;
  animation-delay: 0.2s;
  opacity: 0;
  animation-fill-mode: forwards;
`;

const FormGroup = styled.div`
  margin-bottom: 2rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    color: ${props => props.theme.colors.text};
    font-weight: 500;
  }
  
  input, textarea {
    width: 100%;
    padding: 1rem;
    border: 2px solid #eee;
    border-radius: 10px;
    font-size: 1rem;
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
      box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}22;
    }
  }
  
  textarea {
    min-height: 150px;
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px ${props => props.theme.colors.primary}33;
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    font-size: 1.2rem;
  }
`;

const SuccessMessage = styled.div`
  background: #4CAF50;
  color: white;
  padding: 1rem;
  border-radius: 10px;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: ${fadeUp} 0.5s ease-out;
  
  svg {
    font-size: 1.2rem;
  }
`;

const ErrorMessage = styled.div`
  background: #f44336;
  color: white;
  padding: 1rem;
  border-radius: 10px;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: ${fadeUp} 0.5s ease-out;
  
  svg {
    font-size: 1.2rem;
  }
`;

const ContactPage = () => {
  const form = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Map form field names to state properties
  const fieldNameToState = {
    user_name: 'name',
    user_email: 'email',
    subject: 'subject',
    message: 'message'
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const stateKey = fieldNameToState[name] || name;
    
    setFormData(prev => ({
      ...prev,
      [stateKey]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      
      const result = await emailjs.sendForm(
        'service_0epd9u2',  
        'template_zub3h4k', 
        form.current,
        'FaG4NvlhS0B2UD5JC' 
      );
      
      console.log('Email successfully sent!', result.text);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Failed to send email:', error);
      setSubmitError('Failed to send your message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <BackgroundDesign />
      <SEO
        title="Contact Us | The Big Heart Association | Casablanca, Morocco"
        description="Get in touch with The Big Heart Association (TBH Morocco) in Hay Hassani, Casablanca. Reach us by phone at +212 777794712 or email at asso.thebigheart@tbh.ma."
        url="/contact"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Contact", url: "/contact" }]}
        noOrganizationSchema
      />
      <PageHero>
        <PageHeroContent>
          <PageTitle>Contact <span>Us</span></PageTitle>
          <PageSubtitle>Have questions or want to get involved? We'd love to hear from you. Reach out to us using the form below.</PageSubtitle>
        </PageHeroContent>
      </PageHero>
      <MainContent>
        <ContactContainer>
          <ContactGrid>
            <ContactInfo>
              <InfoTitle>Get in Touch</InfoTitle>
              <InfoList>
                <InfoItem>
                  <div className="icon-wrapper">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                  </div>
                  <div className="content">
                    <h3>Our Location</h3>
                    <p>Hay Hassani, Casablanca</p>
                  </div>
                </InfoItem>
                <InfoItem>
                  <div className="icon-wrapper">
                    <FontAwesomeIcon icon={faPhone} />
                  </div>
                  <div className="content">
                    <h3>Phone Number</h3>
                    <p>+212 777794712</p>
                  </div>
                </InfoItem>
                <InfoItem>
                  <div className="icon-wrapper">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </div>
                  <div className="content">
                    <h3>Email Address</h3>
                    <p>asso.thebigheart@TBH.ma</p>
                  </div>
                </InfoItem>
              </InfoList>
            </ContactInfo>
            <ContactForm ref={form} onSubmit={handleSubmit}>
              {/* EmailJS uses the name attributes to map to email template parameters */}
              <FormGroup>
                <label htmlFor="contact-user_name">Your Name</label>
                <input
                  type="text"
                  id="contact-user_name"
                  name="user_name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your name"
                />
              </FormGroup>
              <FormGroup>
                <label htmlFor="contact-user_email">Email Address</label>
                <input
                  type="email"
                  id="contact-user_email"
                  name="user_email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Enter your email"
                />
              </FormGroup>
              <FormGroup>
                <label htmlFor="contact-subject">Subject</label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="Enter subject"
                />
              </FormGroup>
              <FormGroup>
                <label htmlFor="contact-message">Message</label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Enter your message"
                />
              </FormGroup>
              {/* Hidden field for TBH.ma branding */}
              <input 
                type="hidden" 
                name="from_website" 
                value="TBH.ma" 
              />
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <FontAwesomeIcon icon={faPaperPlane} />
              </SubmitButton>
              {isSubmitted && (
                <SuccessMessage role="status">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Thank you for your message! We'll get back to you soon.
                </SuccessMessage>
              )}
              {submitError && (
                <ErrorMessage role="alert">
                  <FontAwesomeIcon icon={faExclamationCircle} />
                  {submitError}
                </ErrorMessage>
              )}
            </ContactForm>
          </ContactGrid>
        </ContactContainer>
      </MainContent>
    </PageWrapper>
  );
};

export default ContactPage;
